import React, { useCallback, useMemo } from 'react';
import { Input, FormGroup, Label } from 'reactstrap';

import { useGetRegionsQuery, useGetTSPsQuery } from '../services/edgeApi';
import { MultiSelectSearch } from '../helpers/MultiSelect';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store';
import * as filtersSlice from './filtersSlice';
import { selectApiPoints, selectTspOptions } from '../map/slice/mapSelectors';
import { useViewMode } from '../hooks/useViewMode';
import { DevelopmentFlag } from '../helpers/DevelopmentFlag';
import { Region } from '@/types';
// inputs with onchange to dispatch filter changed action

// -- filter change reducer
// save to localstorage
// -- show all options for tsp
// filter by point type
// filter by up/dn
// -- just put this in the sidebar for now but later make a collapsible sidebar]

export const PointFilters = () => {
  const [viewMode] = useViewMode();
  const dispatch = useDispatch();
  const points = useSelector(selectApiPoints);
  const { data: regions } = useGetRegionsQuery(undefined);
  const currentRegionFilters = useSelector(
    (state: RootState) =>
      (state?.filters?.regionAttributes?.id ?? []) as number[],
  );

  // iterate over points, add each ferc cid and tsp name to object with ferc_cid as key - should end up with unique ferc_cids because of the list keys
  const fercCidOptionsObject = useMemo(() => {
    return (
      Object.keys(points)
        .map((pointId) => points[pointId])
        // filter out nulls
        .filter((point) => point.tsp_ferc_cid)
        // add to object, let keys handle uniqueness - don't care much about label, just that key is unique
        .reduce<Record<string, { value: string; label: string }>>(
          (obj, point) => ({
            ...obj,
            [point.tsp_ferc_cid!]: {
              value: point.tsp_ferc_cid!,
              label: point.tsp_name,
            },
          }),
          {},
        )
    );
  }, [points]);

  // map over the object to turn the labels and values into an array of objects
  const fercCidOptions = Object.values(fercCidOptionsObject);

  const onRegionToggle = useCallback(
    (region: Region) => {
      const newValue = currentRegionFilters.includes(region.id)
        ? currentRegionFilters.filter((id) => id !== region.id)
        : [...currentRegionFilters, region.id];
      dispatch(
        filtersSlice.filtersChanged({
          filterType: 'regionAttributes',
          name: 'id',
          value: newValue,
        }),
      );
    },
    [currentRegionFilters, dispatch],
  );

  return (
    <div className="row mx-0 w-100">
      <h6 className="text-center">Point Filters</h6>
      <div className="col-12 overflow-visible" style={{ zIndex: 5 }}>
        <FormGroup>
          <Label>Region</Label>
          <div className="d-flex flex-wrap gap-2">
            {regions
              ?.slice()
              .sort((a, b) => a.id - b.id)
              .map((region) => {
                const isActive = currentRegionFilters.includes?.(region.id);
                return (
                  <button
                    key={region.id.toString()}
                    type="button"
                    className={`btn btn-sm ${
                      isActive ? 'btn-primary' : 'btn-outline-primary'
                    }`}
                    onClick={() => onRegionToggle(region)}
                  >
                    {region.name}
                  </button>
                );
              })}
          </div>
        </FormGroup>

        <FormGroup>
          <Label>TSP</Label>
          <MultiSelectTspFilter
            placement="top"
            keyString="tsp_id"
            filterType="pointAttributes"
          />
        </FormGroup>

        {viewMode === 'development' && (
          <>
            <FormGroup>
              <Label>
                <DevelopmentFlag className="me-2" /> Point Type
              </Label>
              <MultiSelectFilter
                placement="top"
                keyString="loc_type_ind"
                filterType="pointAttributes"
              />
            </FormGroup>

            <FormGroup>
              <Label>
                <DevelopmentFlag className="me-2" /> Connects To
              </Label>
              <MultiSelectFilter
                placement="top"
                keyString="up_dn_ferc_cid"
                customLabelMap={fercCidOptions}
                filterType="pointAttributes"
              />
            </FormGroup>
          </>
        )}
      </div>

      {viewMode === 'development' && (
        <>
          <div className="col-6">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="known-int-filter-checkbox"
              />
              <CheckBoxFilter
                keyString="onlyKnownInterconnects"
                filterType="custom"
              />
              <Label className="form-check-label">
                <DevelopmentFlag className="me-2" /> Only Known Interconnects
              </Label>
            </div>

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="potential-int-filter-checkbox"
              />
              <CheckBoxFilter
                keyString="onlyPotentialInterconnects"
                filterType="custom"
              />
              <Label className="form-check-label">
                <DevelopmentFlag className="me-2" /> Only Potential
                Interconnects
              </Label>
            </div>
          </div>
          <div className="col-6">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="missing-zone-filter-checkbox"
              />
              <CheckBoxFilter keyString="missingZone" filterType="custom" />
              <Label className="form-check-label">
                <DevelopmentFlag className="me-2" /> Missing Zone
              </Label>
            </div>

            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="node-edges-filter-checkbox"
              />
              <CheckBoxFilter keyString="noEdges" filterType="custom" />
              <Label className="form-check-label">
                <DevelopmentFlag className="me-2" /> No Edges
              </Label>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

type MultiSelectTspFilterProps = {
  keyString: string;
  filterType: string;
  placement?: 'top' | 'bottom' | 'auto';
  filterSelection?: string[];
};

const MultiSelectTspFilter = (props: MultiSelectTspFilterProps) => {
  useGetTSPsQuery();
  const dispatch = useDispatch();
  const { filterType } = props;
  const tspOptions = useSelector(selectTspOptions);

  const currentFilters = useSelector(
    (state: RootState) => state.filters[filterType],
  );
  const currentSelection = useMemo(() => {
    try {
      return tspOptions
        .filter((option) =>
          currentFilters[props.keyString]?.includes(option.value),
        )
        .map((option) => option.value);
    } catch (error) {
      // remove filters if they cause some sort of problem.  Should probably be handled closer to the source
      localStorage.removeItem('filters');
      return [];
    }
  }, [tspOptions, currentFilters, props.keyString]);

  const handleChange = useCallback(
    (
      selectedOptions: { label: string; value: string | number }[],
      name: string,
    ) => {
      const value = [...selectedOptions].map((option) => option.value);
      dispatch(filtersSlice.filtersChanged({ name, value, filterType }));
    },
    [dispatch, filterType],
  );

  return (
    <MultiSelectSearch
      placeholder="Pipelines"
      options={tspOptions}
      value={currentSelection}
      onChange={(selectedOptions) =>
        handleChange(selectedOptions, props.keyString)
      }
      menuPlacement={props.placement}
    />
  );
};

type MultiSelectFilterProps = {
  keyString: string;
  filterType: string;
  placement?: 'top' | 'bottom' | 'auto';
  customLabelMap?: { value: string | number; label: string }[];
  filterSelection?: string[];
};

const MultiSelectFilter = (props: MultiSelectFilterProps) => {
  const dispatch = useDispatch();
  const { filterType, keyString, customLabelMap, placement } = props;

  const uniqueValues = useSelector((state: RootState) =>
    // @ts-ignore
    filtersSlice.selectUniqueByKey(state, {
      keyString,
      filterSelection: props.filterSelection,
    }),
  );
  const selectedValues = useSelector(
    (state: RootState) => state.filters?.[filterType]?.[keyString] ?? [],
  );

  const options = (
    customLabelMap ??
    uniqueValues.map((value: string) => ({
      value,
      label: value ?? 'blank',
    }))
  ).sort((a: { label: string }, b: { label: string }) =>
    a.label?.trim().localeCompare(b.label?.trim() ?? ''),
  );

  return (
    <MultiSelectSearch
      options={options}
      value={selectedValues}
      menuPlacement={placement}
      onChange={(selectedOptions) =>
        dispatch(
          filtersSlice.filtersChanged({
            name: keyString,
            value: selectedOptions.map((option) => option.value),
            filterType,
          }),
        )
      }
    />
  );
};

type CheckBoxFilterProps = {
  keyString: string;
  filterType: string;
};

function CheckBoxFilter(props: CheckBoxFilterProps) {
  const { keyString, filterType } = props;
  const dispatch = useDispatch();
  const currentFilters = useSelector((state: RootState) => state.filters);
  const isChecked = currentFilters?.[filterType]?.[keyString];
  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      filtersSlice.filtersChanged({
        name: evt.target.name,
        value: evt.target.checked,
        filterType,
      }),
    );
  };
  return (
    <Input
      type="checkbox"
      name={keyString}
      checked={isChecked || false}
      onChange={handleChange}
    />
  );
}
